import { snapshot as authSnapshot } from "@internal/auth";
import {
  BODY_MEASUREMENT_TYPE,
  type BodyMeasurementEntryData,
  type BodyMeasurementTypeKey,
  bodyMeasurementId,
} from "@/lib/body-measurements";
import { getDb, type PlannerDoc } from "@/lib/db";

function currentUserId(): string {
  const user = authSnapshot().user;
  if (!user) throw new Error("Not signed in");
  return user.id;
}

const writeQueues = new Map<string, Promise<unknown>>();
function enqueue<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const prev = writeQueues.get(key) ?? Promise.resolve();
  const next = prev.then(fn, fn);
  writeQueues.set(
    key,
    next.catch(() => {}),
  );
  return next;
}

/**
 * Upsert one value on the entry for `date`. `null` removes the type's
 * key; rows are never deleted — an entry whose values become empty stays
 * as an empty row (docs/body-measurements-design.md).
 */
export async function setBodyMeasurementValue(
  date: string,
  typeKey: BodyMeasurementTypeKey,
  value: number | null,
): Promise<void> {
  const userId = currentUserId();
  const db = await getDb();
  const id = bodyMeasurementId(userId, date);
  await enqueue(id, async () => {
    const existing = await db.daily_planner.findOne(id).exec();
    const current: BodyMeasurementEntryData = existing
      ? ((existing.toJSON() as PlannerDoc)
          .data as unknown as BodyMeasurementEntryData)
      : { date, values: {} };
    const values = { ...current.values };
    if (value === null) {
      delete values[typeKey];
    } else {
      values[typeKey] = value;
    }
    const next: BodyMeasurementEntryData = { date, values };
    if (existing) {
      await existing.patch({
        data: next as unknown as Record<string, unknown>,
      });
    } else {
      await db.daily_planner.insert({
        id,
        type: BODY_MEASUREMENT_TYPE,
        user_id: userId,
        data: next as unknown as Record<string, unknown>,
      });
    }
  });
}
